import {
    FormControl,
    FormControlLabel,
    Grid,
    Radio,
    RadioGroup,
    Tooltip,
    Typography,
} from "@mui/material";
import { Question } from "components/Admin/Organizations/types/Module.type";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import AcxButton from "components/UI/AcxButton";
import AcxLoadingIndicator from "components/UI/AcxLoadingIndicator";
import AcxToggleButton from "components/UI/AcxToggleButton";
import { observer } from "mobx-react";
import { AnswerPersistenceMode } from "models/AnswerPersistenceMode";
import { ClassifierResultAnswerTag } from "models/ClassifierResultAnswerTag";
import React from "react";
import ClassifierService, {
    QuestionAutoScoreMode,
} from "services/ClassifierService";
import QuestionMetadataService, {
    QuestionMetadataRequest,
} from "services/QuestionMetadataService";
import { useStore } from "utils/useStore";
import OrganizationModuleQuestionMetadataForm, {
    QuestionMetadataFormHandle,
} from "./OrganizationModuleQuestionMetadataForm";
import { OrganizationModuleDetailsStore } from "./OrganizationModuleDetailsStore";
import {
    LabelSmartPredictConfig,
    SmartPredictConfigHandle,
} from "./SmartPredict/LabelSmartPredictConfig";

interface QuestionSettingsDrawerContentProps {
    question: Question;
    organizationId: string;
    // Called after a successful save so the parent can refresh the (otherwise stale) module data.
    onApplied: () => void;
}

const QuestionSettingsDrawerContent: React.FC<QuestionSettingsDrawerContentProps> =
    observer(({ question, organizationId, onApplied }) => {
        const messageStore = useStore(MessageStore);
        const moduleStore = useStore(OrganizationModuleDetailsStore);
        const classifierService = React.useMemo(
            () => new ClassifierService(),
            [],
        );
        const metadataService = React.useMemo(
            () => new QuestionMetadataService(),
            [],
        );

        const [initialPersistenceMode, setInitialPersistenceMode] =
            React.useState<AnswerPersistenceMode>(
                question.automatedAnswerPersistenceMode ??
                    AnswerPersistenceMode.Database,
            );

        const [persistenceMode, setPersistenceMode] =
            React.useState<AnswerPersistenceMode>(initialPersistenceMode);
        const [mode, setMode] = React.useState<QuestionAutoScoreMode>("None");
        // The mode the question loaded with, used to detect whether anything changed.
        const [initialMode, setInitialMode] =
            React.useState<QuestionAutoScoreMode>("None");
        // Bumped after a successful save to remount the active child form so its internal
        // (one-way) dirty state resets and it reloads the now-saved config.
        const [formKey, setFormKey] = React.useState(0);
        const [initializing, setInitializing] = React.useState(true);
        const [isApplying, setIsApplying] = React.useState(false);
        const [smartPredictInvalidReason, setSmartPredictInvalidReason] =
            React.useState<string | null>(null);
        const [metadataInvalidReason, setMetadataInvalidReason] =
            React.useState<string | null>(null);
        const [smartPredictDirty, setSmartPredictDirty] =
            React.useState(false);
        const [metadataDirty, setMetadataDirty] = React.useState(false);

        const smartPredictRef = React.useRef<SmartPredictConfigHandle>(null);
        const metadataRef = React.useRef<QuestionMetadataFormHandle>(null);

        const classifiers = moduleStore.classifiers?.filter(
            (classifier) => classifier.classifierTypeName !== "Metadata",
        );

        // Reflect the question's currently-active feature in the radio selection.
        React.useEffect(() => {
            let cancelled = false;
            const determineMode = async () => {
                try {
                    const [autoScore, metadata] = await Promise.all([
                        classifierService.getAutoScoringConfiguration(
                            organizationId,
                            question.id ?? "",
                        ),
                        metadataService.getQuestionMetadata(organizationId),
                    ]);
                    if (cancelled) return;
                    const hasSmartPredict =
                        Array.isArray(autoScore) && autoScore.length > 0;
                    const hasMetadata = metadata.some(
                        (record) => record.questionId === question.id,
                    );
                    const resolvedMode: QuestionAutoScoreMode = hasSmartPredict
                        ? "SmartPredict"
                        : hasMetadata
                        ? "Metadata"
                        : "None";
                    setMode(resolvedMode);
                    setInitialMode(resolvedMode);
                } catch {
                    if (!cancelled) {
                        messageStore.logError(
                            "Failed to load question settings",
                        );
                    }
                } finally {
                    if (!cancelled) setInitializing(false);
                }
            };

            determineMode();
            return () => {
                cancelled = true;
            };
        }, [
            classifierService,
            metadataService,
            messageStore,
            organizationId,
            question.id,
        ]);

        const changed =
            mode !== initialMode ||
            persistenceMode !== initialPersistenceMode ||
            (mode === "SmartPredict" && smartPredictDirty) ||
            (mode === "Metadata" && metadataDirty);
        const activeInvalidReason =
            mode === "SmartPredict"
                ? smartPredictInvalidReason
                : mode === "Metadata"
                ? metadataInvalidReason
                : null; // None is always complete
        const disableReason =
            activeInvalidReason ?? (!changed ? "No changes to apply" : null);
        const applyDisabled =
            isApplying || initializing || disableReason !== null;

        const handleApply = async () => {
            let classifierResultAnswerTags: ClassifierResultAnswerTag[] = [];
            let questionMetadata: QuestionMetadataRequest | null = null;

            if (mode === "SmartPredict") {
                classifierResultAnswerTags = (
                    smartPredictRef.current?.getMappings() ?? []
                ).map((mapping) => {
                    // The server doesn't want the hydrated nav properties back.
                    const copy: any = { ...mapping };
                    delete copy.classifier;
                    delete copy.classifierOutput;
                    return copy as ClassifierResultAnswerTag;
                });
            } else if (mode === "Metadata") {
                questionMetadata = metadataRef.current?.getPayload() ?? null;
            }

            setIsApplying(true);
            try {
                await classifierService.configureAutoScoring(
                    organizationId,
                    question.id ?? "",
                    {
                        mode,
                        automatedAnswerPersistenceMode: persistenceMode,
                        classifierResultAnswerTags,
                        questionMetadata,
                    },
                );
                messageStore.logInfo("Question settings saved");
                onApplied();
                // The just-saved config becomes the new baseline so the Apply button reflects
                // subsequent changes correctly (e.g. switching back to None to clear it).
                setInitialMode(mode);
                setInitialPersistenceMode(persistenceMode);
                setSmartPredictDirty(false);
                setMetadataDirty(false);
                setFormKey((key) => key + 1);
            } catch {
                messageStore.logError("Failed to save question settings");
            } finally {
                setIsApplying(false);
            }
        };

        return (
            <Grid container rowGap={2} sx={{ paddingTop: "1rem" }}>
                {mode !== "None" && (
                    <Grid item xs={12}>
                        <AcxToggleButton
                            id="question-automated-answer-persistence-mode"
                            inputLabel="Answer Persistence Mode"
                            defaultValue={persistenceMode}
                            exclusive
                            onChange={(value) => {
                                if (value !== null) {
                                    setPersistenceMode(value);
                                }
                            }}
                            items={[
                                {
                                    value: AnswerPersistenceMode.Database,
                                    element: (
                                        <Tooltip title="Answers will be generated and saved during sampling">
                                            <span>Smart Predict</span>
                                        </Tooltip>
                                    ),
                                },
                                {
                                    value: AnswerPersistenceMode.Memory,
                                    element: (
                                        <Tooltip title="Answers will be generated when the module is opened and not persisted until the user saves">
                                            <span>Autofill</span>
                                        </Tooltip>
                                    ),
                                },
                            ]}
                            sx={{ width: "100%" }}
                        />
                    </Grid>
                )}

                {initializing ? (
                    <AcxLoadingIndicator size={32} alternate="PuffLoader" />
                ) : (
                    <>
                        <Grid item xs={12}>
                            <FormControl>
                                <Typography sx={{ fontWeight: 600 }}>
                                    Auto-Scoring
                                </Typography>
                                <RadioGroup
                                    value={mode}
                                    onChange={(_event, value) =>
                                        setMode(value as QuestionAutoScoreMode)
                                    }
                                >
                                    <FormControlLabel
                                        value="None"
                                        control={<Radio />}
                                        label="None"
                                    />
                                    <FormControlLabel
                                        value="SmartPredict"
                                        control={<Radio />}
                                        label="Classifiers (ML, RBC, LLM)"
                                    />
                                    <FormControlLabel
                                        value="Metadata"
                                        control={<Radio />}
                                        label="Metadata"
                                    />
                                </RadioGroup>
                            </FormControl>
                        </Grid>

                        {mode === "SmartPredict" && (
                            <Grid item xs={12}>
                                <LabelSmartPredictConfig
                                    key={formKey}
                                    ref={smartPredictRef}
                                    orgId={organizationId}
                                    question={question}
                                    tags={question.tags ?? []}
                                    classifiers={classifiers}
                                    onValidityChange={
                                        setSmartPredictInvalidReason
                                    }
                                    onDirtyChange={setSmartPredictDirty}
                                />
                            </Grid>
                        )}

                        {mode === "Metadata" && (
                            <Grid item xs={12}>
                                <OrganizationModuleQuestionMetadataForm
                                    key={formKey}
                                    ref={metadataRef}
                                    question={question}
                                    organizationId={organizationId}
                                    onValidityChange={setMetadataInvalidReason}
                                    onDirtyChange={setMetadataDirty}
                                />
                            </Grid>
                        )}

                        <Grid item container xs={12} justifyContent="flex-end">
                            <Tooltip title={disableReason ?? ""}>
                                <span>
                                    <AcxButton
                                        color="primary"
                                        disabled={applyDisabled}
                                        loading={isApplying}
                                        onClick={handleApply}
                                        style={{
                                            padding: "1rem",
                                            marginTop: "1rem",
                                        }}
                                    >
                                        Apply
                                    </AcxButton>
                                </span>
                            </Tooltip>
                        </Grid>
                    </>
                )}
            </Grid>
        );
    });

export default QuestionSettingsDrawerContent;
